import semver from 'semver';
import { getApiUrl } from '../config';

const PLACEHOLDER = '__APP_VERSION__';
const DEV_SENTINEL = '0.0.0-dev';

/**
 * Strip a single leading 'v' and trim whitespace from a version string.
 * @param {string} raw
 * @returns {string}
 */
const normalizeBackendBody = (raw) => {
  const trimmed = String(raw).trim();
  return trimmed.startsWith('v') ? trimmed.slice(1) : trimmed;
};

/**
 * Whether the manifest's version field is a development sentinel that should
 * suppress the banner (placeholder unsubstituted, or explicit dev marker).
 * @param {string} version
 * @returns {boolean}
 */
const isDevVersion = (version) => {
  if (!version || version === PLACEHOLDER || version === DEV_SENTINEL) {
    return true;
  }
  return semver.valid(version) === null;
};

/**
 * Check whether a backend version satisfies any range in the supportedBackends
 * array. Pre-releases are included so wildcards like '0.4.*' match '0.4.0-rc1'.
 * @param {string} backendVersion - normalized SemVer (no leading 'v')
 * @param {string[]} ranges
 * @returns {boolean}
 */
export const matchesAnyRange = (backendVersion, ranges) => {
  if (!Array.isArray(ranges) || ranges.length === 0) {
    return false;
  }
  return ranges.some((range) => {
    try {
      return semver.satisfies(backendVersion, range, { includePrerelease: true });
    } catch (_err) {
      return false;
    }
  });
};

/**
 * Validate the parsed manifest shape per contracts/version-manifest.schema.json.
 * @param {unknown} manifest
 * @returns {boolean}
 */
const isValidManifest = (manifest) => {
  if (!manifest || typeof manifest !== 'object') return false;
  if (typeof manifest.version !== 'string' || manifest.version.length === 0) return false;
  if (!Array.isArray(manifest.supportedBackends) || manifest.supportedBackends.length === 0) return false;
  return manifest.supportedBackends.every(
    (r) => typeof r === 'string' && r.length > 0,
  );
};

/**
 * Evaluate compatibility from a parsed manifest and a raw backend body.
 * Pure function; no I/O. The boundary functions above own the fetches.
 * @param {object|null} manifest - parsed /version.json or null on fetch/parse fail
 * @param {string|null} backendBody - raw text from /_build-info, or null on probe fail
 * @returns {{status: string, appVersion: string|null, backendVersion: string|null, ranges: string[]|null, reason: string|null}}
 */
export const evaluate = (manifest, backendBody) => {
  if (manifest === null) {
    return { status: 'manifest-failed', appVersion: null, backendVersion: null, ranges: null, reason: 'manifest-fetch' };
  }
  if (!isValidManifest(manifest)) {
    return { status: 'manifest-failed', appVersion: null, backendVersion: null, ranges: null, reason: 'manifest-shape' };
  }

  const appVersion = manifest.version;
  const ranges = manifest.supportedBackends;

  if (isDevVersion(appVersion)) {
    return { status: 'dev', appVersion, backendVersion: null, ranges, reason: 'placeholder' };
  }

  if (backendBody === null) {
    return { status: 'probe-failed', appVersion, backendVersion: null, ranges, reason: 'probe-fetch' };
  }

  const normalized = normalizeBackendBody(backendBody);
  const valid = semver.valid(normalized);
  if (valid === null) {
    return { status: 'probe-failed', appVersion, backendVersion: null, ranges, reason: 'probe-parse' };
  }

  if (matchesAnyRange(valid, ranges)) {
    return { status: 'compatible', appVersion, backendVersion: valid, ranges, reason: null };
  }
  return { status: 'incompatible', appVersion, backendVersion: valid, ranges, reason: 'no-match' };
};

/**
 * Fetch /version.json. Returns the parsed object on success, null on any failure.
 * @returns {Promise<object|null>}
 */
export const fetchManifest = async () => {
  try {
    const response = await fetch('/version.json', { cache: 'no-store' });
    if (!response.ok) return null;
    return await response.json();
  } catch (_err) {
    return null;
  }
};

/**
 * Fetch the backend's /_build-info. Returns the raw text body on success,
 * null on any failure (network, non-2xx, etc.).
 * @returns {Promise<string|null>}
 */
export const fetchBackendVersion = async () => {
  try {
    const response = await fetch(getApiUrl('/_build-info'), { cache: 'no-store' });
    if (!response.ok) return null;
    return await response.text();
  } catch (_err) {
    return null;
  }
};

/**
 * Run the full check: fetch manifest and backend version in parallel, then
 * evaluate. Emits a single structured log line and returns the result.
 * @returns {Promise<ReturnType<typeof evaluate>>}
 */
export const runCompatibilityCheck = async () => {
  const [manifest, backendBody] = await Promise.all([
    fetchManifest(),
    fetchBackendVersion(),
  ]);
  const result = evaluate(manifest, backendBody);
  // eslint-disable-next-line no-console
  console.log({ feature: 'version-compat', ...result });
  return result;
};
